import { Accordion, AccordionDetails, AccordionSummary, Box, Paper, Typography } from "@material-ui/core";
import { Controlled as CodeMirror } from "react-codemirror2";
import React from "react";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/neat.css";
import "codemirror/mode/xml/xml.js";
import "codemirror/mode/javascript/javascript.js";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useTranslation } from "react-i18next";

interface TemplateViewer {
    label?: string;
    template: string;
    showTemplatePreview?: boolean;
    templatePreview: string;
    lineWrapping?: boolean;
    disableAccordion?: boolean;
    onSetTemplate(body: string): void;
    readOnly?: boolean;
}

const TemplateBody = ({
    label,
    template,
    showTemplatePreview = true,
    templatePreview,
    onSetTemplate,
    lineWrapping = false,
    readOnly = false,
}: TemplateViewer) => {
    const { t } = useTranslation();

    if (!showTemplatePreview) {
        return (
            <Box component={Paper} mb={2} boxShadow={4}>
                <CodeMirror
                    value={template}
                    selection={{
                        ranges: [
                            {
                                anchor: { ch: 0, line: 0 },
                                head: { ch: 0, line: 0 },
                            },
                        ],
                        focus: true,
                    }}
                    options={{
                        htmlMode: true,
                        lineNumbers: true,
                        lineWrapping,
                        readOnly: readOnly ? "nocursor" : false, // from the lib: "If the special value "nocursor" is given (instead of simply true), focusing of the editor is also disallowed."
                    }}
                    onBeforeChange={(editor, data, value) => {
                        onSetTemplate(value);
                    }}
                />
            </Box>
        );
    }

    return (
        <Box display="flex" flex={1}>
            <Box flex={1} mr={1}>
                {label && (
                    <Box pb={2}>
                        <Typography variant="caption" color="textSecondary">
                            {label}
                        </Typography>
                    </Box>
                )}
                <Box component={Paper} mb={2} boxShadow={4}>
                    <CodeMirror
                        value={template}
                        selection={{
                            ranges: [
                                {
                                    anchor: { ch: 0, line: 0 },
                                    head: { ch: 0, line: 0 },
                                },
                            ],
                            focus: true,
                        }}
                        options={{
                            htmlMode: true,
                            lineNumbers: true,
                            lineWrapping,
                            readOnly: readOnly ? "nocursor" : false, // from the lib: "If the special value "nocursor" is given (instead of simply true), focusing of the editor is also disallowed."
                        }}
                        onBeforeChange={(editor, data, value) => {
                            onSetTemplate(value);
                        }}
                    />
                </Box>
            </Box>
            {showTemplatePreview && (
                <Box flex={1} ml={1}>
                    <Box pb={1}>
                        <Typography variant="caption" color="textSecondary">
                            {t("templates.overview.label.preview")}
                        </Typography>
                    </Box>
                    {templatePreview && (
                        <Box p={2} component={Paper} boxShadow={4}>
                            <Box dangerouslySetInnerHTML={{ __html: templatePreview }} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export const TemplateViewer = ({ disableAccordion, ...props }: TemplateViewer) => {
    const { t } = useTranslation();

    if (disableAccordion) {
        return <TemplateBody {...props} />;
    }

    return (
        <Accordion defaultExpanded variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1" color="textSecondary">
                    {t("templates.overview.label.templateDesign")}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <TemplateBody {...props} />
            </AccordionDetails>
        </Accordion>
    );
};
